import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

function AdditionalSettingCounter({ selected, formik, onClose }) {
  const { t } = useTranslation();
  let values = formik.values.additional_items;
  const [initialData, setInitialData] = useState(JSON.parse(JSON.stringify(values)));

  const totalItems = initialData.reduce((sum, item) => sum + (item[selected.value] || 0), 0);
  const maxItems = selected.value === "baby_seat" ? 5 : 10;

  const handleIncrement = (idx) => {
    let arr = [...initialData];
    if (selected.value === "baby_seat" && arr[idx][selected.value] >= 5) return;
    arr[idx][selected.value] = arr[idx][selected.value] + 1;
    setInitialData(arr);
  };

  const handleDecrement = (idx) => {
    let arr = [...initialData];
    if (arr[idx][selected.value] === 0) return;
    arr[idx][selected.value] = arr[idx][selected.value] - 1;
    setInitialData(arr);
  };

  const handleSetValue = (idx, value) => {
    let arr = [...initialData];
    const numValue = parseInt(value) || 0;
    if (selected.value === "baby_seat" && numValue > 5) return;
    arr[idx][selected.value] = numValue;
    setInitialData(arr);
  };

  return (
    <div className="p-6 bg-white rounded-sm md:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-[#f5272b] to-red-600 rounded-xl flex items-center justify-center shadow-lg">
            <i className="text-white fa-solid fa-calculator"></i>
          </div>
          <h3 className="text-xl font-bold md:text-2xl text-slate-900">
            {t("setting_date_use")} {selected.title}
          </h3>
        </div>
        <p className="text-sm text-slate-600 ml-13">{t("set_quantity_each_date")}</p>
      </div>

      {/* Summary */}
      <div className="p-4 mb-6 border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 bg-white shadow-md rounded-xl">
              <i className="text-xl text-purple-600 fa-solid fa-box"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">{t("total_items")}</p>
              <p className="text-2xl font-bold text-purple-700">{totalItems}</p>
            </div>
          </div>
          {selected.value === "baby_seat" && (
            <div className="text-right">
              <p className="text-xs font-medium text-slate-600">Maximum per day</p>
              <p className="text-lg font-bold text-slate-900">{maxItems}</p>
            </div>
          )}
        </div>
      </div>

      {/* Date List with Counters */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto mb-6 pr-2">
        {initialData?.map((item, idx) => {
          const dateFormatted = moment(item.date).format("ddd, DD MMM YYYY");
          const currentValue = item[selected.value];
          const hasValue = currentValue > 0;

          return (
            <div
              key={idx}
              className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all duration-300 ${
                hasValue
                  ? "bg-gradient-to-r from-blue-50 to-blue-100 border-blue-300 shadow-md"
                  : "bg-white border-slate-200"
              }`}
            >
              {/* Date Info */}
              <div className="flex items-center flex-1 min-w-0 gap-4">
                <div
                  className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm ${
                    hasValue ? "bg-blue-500 text-white shadow-lg shadow-blue-500/30" : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {idx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold text-sm ${hasValue ? "text-blue-900" : "text-slate-900"}`}>
                    {dateFormatted}
                  </p>
                  {hasValue && (
                    <p className="text-xs text-blue-600 mt-0.5">
                      <i className="mr-1 fa-solid fa-box-open"></i>
                      {currentValue} {currentValue === 1 ? "item" : "items"}
                    </p>
                  )}
                </div>
              </div>

              {/* Counter Controls */}
              <div className="flex items-center flex-shrink-0 gap-2">
                <button
                  type="button"
                  onClick={() => handleDecrement(idx)}
                  disabled={currentValue === 0}
                  className={`w-10 h-10 rounded-lg font-bold text-lg transition-all duration-300 ${
                    currentValue === 0
                      ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                      : "bg-[#f5272b] hover:bg-red-700 text-white shadow-lg"
                  }`}
                >
                  <i className="text-sm fa-solid fa-minus"></i>
                </button>

                <input
                  type="number"
                  value={currentValue}
                  onChange={(e) => handleSetValue(idx, e.target.value)}
                  className="w-16 h-10 text-center font-bold text-lg bg-white border-2 border-slate-300 rounded-lg focus:border-[#f5272b] focus:ring-2 focus:ring-[#f5272b]/20 transition-all"
                  min="0"
                  max={maxItems}
                />

                <button
                  type="button"
                  onClick={() => handleIncrement(idx)}
                  disabled={selected.value === "baby_seat" && currentValue >= 5}
                  className={`w-10 h-10 rounded-lg font-bold text-lg transition-all duration-300 ${
                    selected.value === "baby_seat" && currentValue >= 5
                      ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                      : "bg-[#f5272b] hover:bg-red-700 text-white shadow-lg"
                  }`}
                >
                  <i className="text-sm fa-solid fa-plus"></i>
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Alert */}
      {selected.value === "baby_seat" && (
        <div className="flex items-start gap-3 p-4 mb-6 border-2 bg-amber-50 border-amber-200 rounded-xl">
          <i className="fa-solid fa-circle-info text-amber-600 text-lg mt-0.5 flex-shrink-0"></i>
          <p className="text-sm leading-relaxed text-amber-900">
            Maximum {maxItems} baby seats per day for safety regulations
          </p>
        </div>
      )}

      {/* Footer Buttons */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={() => {
            setInitialData([]);
            onClose();
          }}
          className="flex-1 px-6 py-3 font-semibold transition-all duration-300 border bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl border-slate-200"
        >
          <i className="mr-2 fa-solid fa-xmark"></i>
          {t("cancel")}
        </button>
        <button
          type="button"
          onClick={() => {
            formik.setFieldValue("additional_items", initialData);
            onClose();
          }}
          className="flex-1 px-6 py-3 bg-[#f5272b] hover:bg-red-700 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-[#f5272b]/30"
        >
          <i className="mr-2 fa-solid fa-check"></i>
          {t("save")} ({totalItems} items)
        </button>
      </div>
    </div>
  );
}

export default AdditionalSettingCounter;
